import {
  Checkbox,
  Divider,
  Tag,
  Flex,
  FormControl,
  FormHelperText,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { auth } from "../../core/auth";
import EntityModalButton from "../entityProfileModal/components/EntityModalButton";
import useProfileWindowSettings from "../profileWindowSettings/useProfileWindowSettings";
import { BETA_FEATURE_PERMISSION as FEATURE_PERMISSION } from "./betaFeatures.permissions";
import useBetaFeatures from "./useBetaFeatures";

type Props = {
  onClose: () => void;
};

function BetaFeature(props: {
  children: React.ReactNode;
  /** What it does, and where it might fail. Not how it works. */
  helperText: string;
  isChecked: boolean;
  /** False when the caller's role does not give them the thing this switches. */
  isAvailable: boolean;
  label: string;
  /** When the switch is felt - beside the name, quietly, because it qualifies the name. */
  takesEffect: string;
  unavailableText: string;
  onChange: (isChecked: boolean) => void;
}) {
  return (
    <Flex direction="column" gap={2}>
      <Flex align="baseline" gap={2} wrap="wrap">
        <Text color={props.isAvailable ? undefined : "gray.500"} fontSize="14px" fontWeight={700}>
          {props.label}
        </Text>
        <Text color="gray.500" fontSize="12px" fontWeight={400}>
          {props.takesEffect}
        </Text>
        {props.isAvailable ? null : (
          <Tag colorScheme="gray" size="sm">
            Not available
          </Tag>
        )}
      </Flex>
      <FormControl isDisabled={!props.isAvailable}>
        <Checkbox
          isChecked={props.isAvailable && props.isChecked}
          isDisabled={!props.isAvailable}
          onChange={(event) => props.onChange(event.target.checked)}
        >
          {props.children}
        </Checkbox>
        <FormHelperText mt={1}>
          {props.isAvailable ? props.helperText : props.unavailableText}
        </FormHelperText>
      </FormControl>
    </Flex>
  );
}

/**
 * One place to turn the in-flight features on and off, reached from the settings
 * flyout in the sidebar rather than from inside any one screen — these cut
 * across screens, so none of them owns the switch.
 *
 * The profile window's own options (which section to open on, fold state,
 * calendar hover) stay in the profile window settings modal, where they sit next
 * to the window they describe. Only the on/off switch is here.
 */
export default function BetaFeaturesModal(props: Props) {
  const [features, updateFeatures] = useBetaFeatures();
  const [windowSettings, updateWindowSettings] = useProfileWindowSettings();

  // Read on render rather than memoised: two localStorage reads, and a cached
  // answer could only be a stale one.
  const canUseCaregiverImprovements = auth.isAllowedTo(
    FEATURE_PERMISSION.caregiverMemoryImprovements
  );
  const canUsePatientImprovements = auth.isAllowedTo(FEATURE_PERMISSION.patientMemoryImprovements);
  const canUseBetaWindow = auth.isAllowedTo(FEATURE_PERMISSION.betaProfileWindow);
  const canUseAiSearch = auth.isAllowedTo(FEATURE_PERMISSION.aiSearch);

  return (
    <Modal isOpen size="xl" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="18px">Beta features</ModalHeader>
        <Divider />
        <ModalCloseButton onClick={props.onClose} />
        <ModalBody>
          <Flex direction="column" gap={5} py={2}>
            <Text color="gray.600" fontSize="13px">
              These apply to your browser only, and can be turned back off at any time.
            </Text>

            <Divider />

            <BetaFeature
              helperText="Makes the caregivers page load faster. It is a new list, so a caregiver or some of their details may not show up — turn it off to go back to the old one."
              isAvailable={canUseCaregiverImprovements}
              isChecked={features.caregiverMemoryImprovements}
              label="Caregiver memory improvements"
              takesEffect="Takes effect on the next page load"
              unavailableText="Your role does not have access to this beta feature. An administrator can grant it under Beta Features in the role's permissions."
              onChange={(isChecked) => updateFeatures({ caregiverMemoryImprovements: isChecked })}
            >
              <Text fontWeight={600}>Use the caregiver memory improvements</Text>
            </BetaFeature>

            <Divider />

            <BetaFeature
              helperText="Makes the patients page load faster. It is a new list, so a patient or some of their details may not show up — turn it off to go back to the old one."
              isAvailable={canUsePatientImprovements}
              isChecked={features.patientMemoryImprovements}
              label="Patient memory improvements"
              takesEffect="Takes effect on the next page load"
              unavailableText="Your role does not have access to this beta feature. An administrator can grant it under Beta Features in the role's permissions."
              onChange={(isChecked) => updateFeatures({ patientMemoryImprovements: isChecked })}
            >
              <Text fontWeight={600}>Use the patient memory improvements</Text>
            </BetaFeature>

            <Divider />

            <BetaFeature
              helperText="Opens patient and caregiver profiles in the new window. Parts of a profile may look or behave differently there — turn it off to go back to the old window."
              isAvailable={canUseBetaWindow}
              isChecked={windowSettings.useBetaWindow}
              label="Caregiver and patient window"
              takesEffect="Takes effect next time you open a profile"
              unavailableText="Your role does not have access to this beta feature. An administrator can grant it under Beta Features in the role's permissions."
              onChange={(isChecked) => updateWindowSettings({ useBetaWindow: isChecked })}
            >
              <Text fontWeight={600}>Use the new (beta) profile window</Text>
            </BetaFeature>

            <Divider />

            <BetaFeature
              helperText="Adds an AI mode to the search bar: ask for what you want to see in words, and it opens the page and sets the filters. It can open the wrong page or leave a filter empty, so read the filters before you run the search."
              isAvailable={canUseAiSearch}
              isChecked={features.aiSearch}
              label="AI search"
              takesEffect="Takes effect on the next page load"
              unavailableText="Your role does not have access to this beta feature. An administrator can grant it under Beta Features in the role's permissions."
              onChange={(isChecked) => updateFeatures({ aiSearch: isChecked })}
            >
              <Text fontWeight={600}>Use AI search</Text>
            </BetaFeature>
          </Flex>
        </ModalBody>
        <Divider />
        <ModalFooter>
          <EntityModalButton kind="blue" size="sm" onClick={props.onClose}>
            Done
          </EntityModalButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
